import React, { useState, useEffect, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import API_BASE_URL from '../config/apiConfig';

const LoginForm = ({ isVisible, onForgotPassword, onLoginSubmit, onRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Track previous isVisible value
  const prevIsVisibleRef = useRef(isVisible);

  // Google Login handler
  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      setIsLoading(true);
      setError('');
      
      // Decode JWT token từ Google để lấy thông tin user
      const base64Url = credentialResponse.credential.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const userInfo = JSON.parse(jsonPayload);
      
      // Gửi thông tin lên backend
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userInfo.email,
          name: userInfo.name || userInfo.email,
          googleId: userInfo.sub,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Lưu JWT token và thông tin user
        if (data.token) {
          localStorage.setItem('jwt_token', data.token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isLoggedIn', 'true');
        
        // Dispatch event to update sidebar
        window.dispatchEvent(new Event('userProfileUpdated'));
        
        // Reset loading state before navigation
        setIsLoading(false);
        
        // Chuyển đến dashboard
        if (onLoginSubmit) onLoginSubmit();
      } else {
        setError(data.message || 'Google login failed');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Google login error:', error);
      setError('Google login error. Please try again.');
      setIsLoading(false);
    }
  };

  // Use Google Login hook
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      // Get user info from Google
      try {
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });
        const userInfo = await userInfoResponse.json();
        
        // Send to backend
        const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userInfo.email,
            name: userInfo.name || userInfo.email,
            googleId: userInfo.id,
          }),
        });

        const data = await response.json();

        if (data.success) {
          if (data.token) {
            localStorage.setItem('jwt_token', data.token);
          }
          localStorage.setItem('user', JSON.stringify(data.user));
          localStorage.setItem('isLoggedIn', 'true');
          
          // Dispatch event to update sidebar
          window.dispatchEvent(new Event('userProfileUpdated'));
          
          setIsLoading(false);
          if (onLoginSubmit) onLoginSubmit();
        } else {
          setError(data.message || 'Google login failed');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Google login error:', error);
        setError('Google login error. Please try again.');
        setIsLoading(false);
      }
    },
    onError: () => {
      setError('Google login failed');
      setIsLoading(false);
    },
  });

  // Function để reset tất cả state
  const resetAllState = () => {
    setEmail('');
    setPassword('');
    setIsLoading(false);
    setError('');
    setShowPassword(false);
  };

  // Reset state khi component được hiển thị hoặc khi isVisible thay đổi
  useEffect(() => {
    if (isVisible) {
      // Always reset when component becomes visible
      resetAllState();
      
      // Also check if user is logged out (no token in localStorage)
      const isLoggedIn = localStorage.getItem('isLoggedIn');
      if (isLoggedIn !== 'true') {
        // User is logged out, ensure all state is reset
        resetAllState();
      }
    }
  }, [isVisible]);

  // Reset khi component bị ẩn (khi logout)
  useEffect(() => {
    if (!isVisible && prevIsVisibleRef.current) {
      // Component bị ẩn, reset state immediately
      resetAllState();
    }
    prevIsVisibleRef.current = isVisible;
  }, [isVisible]);

  // Additional check: reset if localStorage shows user is logged out
  useEffect(() => {
    if (isVisible) {
      const checkLoggedOut = () => {
        const isLoggedIn = localStorage.getItem('isLoggedIn');
        if (isLoggedIn !== 'true' && isLoading) {
          // User logged out but isLoading is still true, reset it
          setIsLoading(false);
        }
      };
      // Check immediately
      checkLoggedOut();
      // Check periodically while visible
      const interval = setInterval(checkLoggedOut, 100);
      return () => clearInterval(interval);
    }
  }, [isVisible, isLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success) {
        // Lưu JWT token và thông tin user vào localStorage
        if (data.token) {
          localStorage.setItem('jwt_token', data.token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isLoggedIn', 'true');
        
        // Dispatch event to update sidebar
        window.dispatchEvent(new Event('userProfileUpdated'));
        
        // Gọi hàm chuyển sang trang tiếp theo
        if (onLoginSubmit) onLoginSubmit();
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`login-container ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="form-box">
        <div className="header-text">
          <h2>Login</h2>
          <p>Welcome back! Please enter your details.</p>
        </div>
        
        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            background: '#fee2e2',
            color: '#dc2626',
            borderRadius: '8px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Email Input */}
          <div className="input-group">
            <input 
              type="email" 
              placeholder="joe.doe@gmail.com" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          {/* Password Input */}
          <div className="input-group password-group">
            <input 
              type={showPassword ? "text" : "password"} 
              placeholder="Password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
            <span 
              className="eye-icon" 
              onClick={() => setShowPassword(!showPassword)}
              style={{ cursor: 'pointer' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {showPassword ? (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </>
                ) : (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </>
                )}
              </svg>
            </span>
          </div>
          
          {/* Remember Me */}
          <div className="remember-box">
             <label className="checkbox-container">
               <input type="checkbox" /> 
               <span className="checkmark"></span>
               Remember information
             </label>
          </div>

          {/* Login Button */}
          <button type="submit" className="login-btn" disabled={isLoading}>
            {isLoading ? 'Login...' : 'Login'}
          </button>
          
<div className="forgot-pass">
           {/* Thêm sự kiện onClick */}
           <a href="#" onClick={(e) => {
             e.preventDefault();
             onForgotPassword();
           }}>Forgot password?</a>
        </div>
          {/* Divider OR */}
          <div className="divider">
            <span>or</span>
          </div>

          {/* Social Login Buttons */}
          <div className="social-buttons">
            <button 
              className="social-btn"
              type="button"
              disabled={isLoading}
              onClick={() => {
                setIsLoading(true);
                setError('');
                googleLogin();
              }}
            >
              {/* Google SVG */}
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Login with Google</span>
            </button>

            <button className="social-btn">
              {/* Facebook SVG */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              <span>Login with Facebook</span>
            </button>

            <button className="social-btn">
              {/* Apple SVG */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="black"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.35-1.09-.56-2.09-.48-3.08.35-1.04.86-2.17.79-3.13-.54-2.13-2.96-1.54-6.86 1.35-8.32 1.54-.78 2.89-.16 3.8.19.89.34 2.17.34 3.16-.31.62-.41 1.66-.46 2.19.34.62.94.39 1.86.06 2.85-2.05 2.83-2.17 2.92-2.19 2.96-.32.49-.6.98-.89 1.45-.63 1.05-1.05 1.82 1.81 3.67zm-2.95-16.1c.36-.08.79.06 1.13.52.4.53.37 1.25-.09 1.7-.36.35-.91.56-1.3.52-.36-.04-.69-.4-1.05-.93-.34-.49-.3-1.25.09-1.63.43-.43.91-.32 1.22-.18z"/></svg>
              <span>Login with Apple</span>
            </button>
          </div>

          <div className="signup-text">
            First time here? <a href="#" onClick={(e) => {
              e.preventDefault();
              if (onRegister) onRegister();
            }}>Sign up for free</a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;